import { Decimal, bn } from '../core/BigNumber';

const SUFFIXES = [
  '', 'million', 'billion', 'trillion', 'quadrillion', 'quintillion',
  'sextillion', 'septillion', 'octillion', 'nonillion', 'decillion',
  'undecillion', 'duodecillion', 'tredecillion', 'quattuordecillion',
  'quindecillion', 'sexdecillion', 'septendecillion', 'octodecillion',
  'novemdecillion', 'vigintillion',
];

const SHORT_SUFFIXES = [
  '', 'M', 'B', 'T', 'Qa', 'Qi', 'Sx', 'Sp', 'Oc', 'No', 'Dc',
  'UDc', 'DDc', 'TDc', 'QaDc', 'QiDc', 'SxDc', 'SpDc', 'OcDc',
  'NoDc', 'Vg',
];

export function beautify(num: Decimal | number, decimals = 1): string {
  const d = num instanceof Decimal ? num : bn(num);

  if (d.lt(1e6)) {
    const n = d.toNumber();
    if (Number.isInteger(n)) return n.toLocaleString('en-US');
    return n.toLocaleString('en-US', {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    });
  }

  const exp = Math.floor(d.e / 3);
  const idx = Math.min(exp - 1, SUFFIXES.length - 1);

  if (idx < SUFFIXES.length && idx >= 0) {
    const mantissa = d.div(bn(10).pow(idx * 3 + 3)).toNumber();
    return `${mantissa.toFixed(3)} ${SUFFIXES[idx]}`;
  }

  return d.toExponential(3);
}

export function shortBeautify(num: Decimal | number): string {
  const d = num instanceof Decimal ? num : bn(num);

  if (d.lt(1e6)) {
    return Math.floor(d.toNumber()).toLocaleString('en-US');
  }

  const exp = Math.floor(d.e / 3);
  const idx = Math.min(exp - 1, SHORT_SUFFIXES.length - 1);

  if (idx < SHORT_SUFFIXES.length && idx >= 0) {
    const mantissa = d.div(bn(10).pow(idx * 3 + 3)).toNumber();
    return `${mantissa.toFixed(1)} ${SHORT_SUFFIXES[idx]}`;
  }

  return d.toExponential(2);
}

export function formatTime(ms: number): string {
  const seconds = Math.floor(ms / 1000);
  if (seconds < 60) return `${seconds}s`;

  const minutes = Math.floor(seconds / 60);
  const secs = seconds % 60;
  if (minutes < 60) return `${minutes}m ${secs}s`;

  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  if (hours < 24) return `${hours}h ${mins}m`;

  const days = Math.floor(hours / 24);
  const hrs = hours % 24;
  return `${days}d ${hrs}h`;
}

export function formatPercent(value: number, decimals = 1): string {
  return `${(value * 100).toFixed(decimals)}%`;
}
